import { Component } from '@theme/component';

/**
 * Component refs
 * @typedef {Object} Refs
 * @property {HTMLElement} locationsList - Main locations list
 */

/**
 * Location list component for state navigation and scroll tracking.
 * @extends {Component<Refs>}
 */
class CLocationSearchComponent extends Component {
  requiredRefs = [];

  /** @type {HTMLElement[]} */
  #stateNavLinks = [];

  /** @type {HTMLElement[]} */
  #stateGroups = [];

  /** @type {IntersectionObserver|null} */
  #scrollObserver = null;

  /** @type {boolean} */
  #isScrollingFromClick = false;

  connectedCallback() {
    super.connectedCallback();
    this.#initStateNavigation();
  }

  /**
   * Initialize state navigation (sidebar links and scroll tracking)
   */
  #initStateNavigation() {
    this.#stateNavLinks = Array.from(this.querySelectorAll('.c-locations-list__state-nav-link'));
    this.#stateGroups = Array.from(this.querySelectorAll('.c-locations-list__state-group'));

    // Bind click handlers to state nav links
    this.#stateNavLinks.forEach(link => {
      link.addEventListener('click', this.#handleStateNavClick);
    });

    // Set first state as active by default
    if (this.#stateNavLinks.length > 0) {
      this.#setActiveState(this.#stateNavLinks[0]);
    }

    // Use Intersection Observer to track which state is in view
    this.#initScrollObserver();
  }

  /**
   * Initialize Intersection Observer for scroll-based active state updates
   */
  #initScrollObserver() {
    if (this.#stateGroups.length === 0) return;

    this.#scrollObserver = new IntersectionObserver(
      (entries) => {
        // Skip updates if user clicked a nav link (let click handler control active state)
        if (this.#isScrollingFromClick) return;

        // Find the entry that is most visible in the viewport
        let mostVisibleEntry = null;
        let highestRatio = 0;

        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio > highestRatio) {
            highestRatio = entry.intersectionRatio;
            mostVisibleEntry = entry;
          }
        }

        // If nothing is intersecting with high ratio, find the one closest to top of viewport
        if (!mostVisibleEntry) {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              mostVisibleEntry = entry;
              break;
            }
          }
        }

        if (mostVisibleEntry) {
          const state = mostVisibleEntry.target.getAttribute('data-state');
          const activeLink = this.#stateNavLinks.find(link => link.getAttribute('data-state') === state);
          if (activeLink) {
            this.#setActiveState(activeLink);
          }
        }
      },
      {
        root: null, // Use viewport as root
        rootMargin: '-20% 0px -60% 0px', // Trigger when element is in upper portion of viewport
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5]
      }
    );

    // Observe all state groups
    this.#stateGroups.forEach(group => {
      this.#scrollObserver?.observe(group);
    });
  }

  /**
   * Handle state nav link click
   * @param {MouseEvent} event
   */
  #handleStateNavClick = (event) => {
    const link = event.currentTarget;
    if (link instanceof HTMLElement) {
      this.#setActiveState(link);

      // Temporarily disable scroll observer updates while scrolling from click
      this.#isScrollingFromClick = true;
      setTimeout(() => {
        this.#isScrollingFromClick = false;
      }, 1000);
    }
  };

  /**
   * Set active state in sidebar
   * @param {HTMLElement} activeLink
   */
  #setActiveState(activeLink) {
    this.#stateNavLinks.forEach(link => {
      link.classList.remove('is-active');
    });
    activeLink.classList.add('is-active');
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Clean up Intersection Observer
    if (this.#scrollObserver) {
      this.#scrollObserver.disconnect();
      this.#scrollObserver = null;
    }
  }
}

customElements.define('c-location-search-component', CLocationSearchComponent);
